//get all employees

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getEmployees,
  createEmployee,
  EmployeeFormData,
  updateEmployee,
  deleteEmployee,
  getEmployeeRoleIsSeller,
  changeStatusEmployee,
  userStatus,
  changePassword,
  ChangePasswordFormData,
} from '../api/employee';
import { IEmployee } from '../api/employee';
import { toast } from 'react-toastify';
import { RoleDto } from './useRole';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from './useAuth';
export interface EmployeeDTO {
  id: string;
  username: string;
  email: string;
  phone: string;
  address: string;
  dateOfBirth: string;
  role: RoleDto;
  createdAt: string;
  updatedAt: string;
  status: userStatus;
  customerCount: number;
}

export const parseEmployeeDTO = (employee: IEmployee): EmployeeDTO => {
  return {
    id: employee.id,
    username: employee.name,
    email: employee.email,
    phone: employee.phoneNumber,
    address: employee.address,
    dateOfBirth: employee.dateOfBirth,
    role: {
      id: employee.role.id,
      name: employee.role.name,
      key: employee.role.key,
      permissions: employee.role.permissions,
    },
    createdAt: employee.createdAt,
    updatedAt: employee.updatedAt,
    status: employee.status,
    customerCount: employee.customerCount,
  };
};

//có phân trang

export const useEmployees = ({
  page,
  limit,
  search,
  status,
  roleKey,
}: {
  page: number;
  limit: number;
  search: string;
  status?: userStatus;
  roleKey?: string;
}) => {
  return useQuery<{ employees: EmployeeDTO[]; total: number; pages: number }>({
    queryKey: ['employees', page, limit, search, status, roleKey],
    queryFn: async () => {
      const response = await getEmployees(page, limit, search, status, roleKey);
      const employees = response.items.map((item) => parseEmployeeDTO(item));
      return {
        employees,
        total: response.totalItems,
        pages: response.totalPages,
        hasNextPage: response.hasNextPage,
      };
    },
    placeholderData: (previousData) => previousData,
  });
};

//get employee by id

//create employee

export const useCreateEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: EmployeeFormData) => {
      const response = await createEmployee(data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      toast.success('Tạo nhân viên thành công');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};
//update employee

export const useUpdateEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: EmployeeFormData;
    }) => {
      const response = await updateEmployee(id, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      toast.success('Cập nhật nhân viên thành công');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

//delete employee

export const useDeleteEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await deleteEmployee(id);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      toast.success('Xóa nhân viên thành công');
    },
    onError: (error) => {
      toast.error(error.message);
    },
    retry: false,
  });
};

export const useGetEmployeeRoleIsSeller = (isSeller: boolean) => {
  return useQuery({
    queryKey: ['employee-role-is-seller'],
    queryFn: async () => {
      const response = await getEmployeeRoleIsSeller();
      return response;
    },
    enabled: !isSeller,
  });
};

export const useChangeStatusEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      status,
      idEmployeeNew,
    }: {
      id: string;
      status: userStatus;
      idEmployeeNew?: number;
    }) => {
      queryClient
        .getQueryCache()
        .findAll({ queryKey: ['employees'] })
        .forEach((query) => {
          const data = query.state.data as {
            employees: EmployeeDTO[];
            total: number;
            pages: number;
            hasNextPage?: boolean;
          };
          if (data && Array.isArray(data.employees)) {
            queryClient.setQueryData(query.queryKey, {
              ...data,
              employees: data.employees.map((employee: EmployeeDTO) =>
                employee.id === id ? { ...employee, status: status } : employee
              ),
            });
          }
        });
      toast.success('Cập nhật trạng thái nhân viên thành công');
      const response = await changeStatusEmployee(id, status, idEmployeeNew);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-role-is-seller'] });
    },
    onError: (error, { id, status }) => {
      toast.error(error.message);
      queryClient
        .getQueryCache()
        .findAll({ queryKey: ['employees'] })
        .forEach((query) => {
          const data = query.state.data as {
            employees: EmployeeDTO[];
            total: number;
            pages: number;
            hasNextPage?: boolean;
          };
          if (data && Array.isArray(data.employees)) {
            queryClient.setQueryData(query.queryKey, {
              ...data,
              employees: data.employees.map((employee: EmployeeDTO) =>
                employee.id === id ? { ...employee, status: status } : employee
              ),
            });
          }
        });
    },
  });
};

export const useChangePassword = () => {
  const queryClient = useQueryClient();
  const setIsAuthenticated = useAuthStore((state) => state.setIsAuthenticated);
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (data: ChangePasswordFormData) => {
      const response = await changePassword(data);
      return response;
    },
    onSuccess: () => {
      toast.success('Đổi mật khẩu thành công. Bạn vui lòng đăng nhập lại');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('isAuthenticated');
      setIsAuthenticated(false);
      queryClient.setQueryData(['auth'], false);
      queryClient.clear();
      navigate('/login');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};
